import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { getPricePerPixel } from '../utils/pricing';
import * as FiIcons from 'react-icons/fi';
const { FiMove, FiMaximize, FiZap, FiTrendingUp, FiBarChart2, FiInfo, FiX, FiShield, FiLink, FiUser, FiLogOut, FiGrid, FiDollarSign } = FiIcons;

function ModeToggle({ mode, setMode }) {
  return (
    <div className="flex rounded-xl overflow-hidden border border-gray-700 flex-1">
      <button onClick={() => setMode('pan')} className="flex-1 py-3 font-black flex items-center justify-center gap-2 transition-all"
        style={{ fontSize: '14px', background: mode === 'pan' ? 'rgba(255,0,200,0.2)' : 'transparent', color: mode === 'pan' ? '#ff00cc' : '#6b7280' }}>
        <FiMove /> PAN
      </button>
      <button onClick={() => setMode('select')} className="flex-1 py-3 font-black flex items-center justify-center gap-2 transition-all"
        style={{ fontSize: '14px', background: mode === 'select' ? 'rgba(255,0,200,0.2)' : 'transparent', color: mode === 'select' ? '#ff00cc' : '#6b7280' }}>
        <FiMaximize /> SELECT
      </button>
    </div>
  );
}

function StatsSheet({ onClose }) {
  const { tiles, pixelsSold, setCurrentPage, user, signOut, setIsAuthModalOpen } = useAppContext();
  const topTiles = [...tiles].filter(t => t.title && t.link).sort((a, b) => (b.w * b.h) - (a.w * a.h)).slice(0, 5);
  const percentSold = ((pixelsSold / 1000000) * 100).toFixed(1);
  const rankColors = ['#FFD700', '#C0C0C0', '#CD7F32', '#ff00cc', '#ff00cc'];

  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end" onClick={onClose}>
      <div className="rounded-t-2xl overflow-y-auto" style={{ background: '#0a0014', border: '1px solid rgba(255,0,200,0.3)', maxHeight: '85vh' }} onClick={e => e.stopPropagation()}>
        <div className="flex justify-center pt-3 pb-1"><div className="w-10 h-1 rounded-full bg-gray-700" /></div>
        <div className="px-5 pb-6 space-y-5">
          {/* Progress */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-400 font-bold uppercase tracking-widest" style={{ fontSize: '12px' }}>Grid Filled</span>
              <span className="font-black" style={{ color: '#ff00cc', fontSize: '16px' }}>{percentSold}%</span>
            </div>
            <div className="h-2.5 bg-gray-900 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${percentSold}%`, background: 'linear-gradient(90deg,#ff00cc,#cc00ff)' }} />
            </div>
            <div className="text-gray-500 mt-1" style={{ fontSize: '13px' }}>{(1000000 - pixelsSold).toLocaleString()} px remaining</div>
          </div>

          {/* Affiliate CTA */}
          <button onClick={() => { setCurrentPage('affiliate'); onClose(); }}
            className="w-full flex items-center gap-3 p-3 rounded-xl text-left"
            style={{ background: 'rgba(0,255,136,0.06)', border: '1px solid rgba(0,255,136,0.25)' }}>
            <FiDollarSign className="text-green-400 text-lg shrink-0" />
            <div className="flex-1">
              <div className="font-black text-white text-sm">Earn 20% Commission</div>
              <div className="text-gray-500 text-xs">Refer friends, get paid on every sale</div>
            </div>
            <FiZap className="text-green-400 shrink-0" />
          </button>

          {/* Top tiles */}
          <div>
            <div className="font-black text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-1" style={{ fontSize: '12px' }}>
              <FiTrendingUp className="text-pink-500" /> Top By Pixels
            </div>
            <div className="space-y-2.5">
              {topTiles.map((tile, idx) => (
                <div key={tile.id} className="flex items-center gap-3 cursor-pointer"
                  onClick={() => tile.link && window.open(tile.link, '_blank', 'noopener,noreferrer')}>
                  <div className="w-6 text-center font-black" style={{ fontSize: '14px', color: rankColors[idx] }}>{idx + 1}</div>
                  <div className="w-9 h-9 rounded-lg overflow-hidden shrink-0 border border-gray-700" style={{ background: tile.solidColor || '#1a0030' }}>
                    {tile.image ? <img src={tile.image} alt={tile.title} className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white truncate" style={{ fontSize: '15px' }}>{tile.title}</div>
                    <div className="text-gray-500" style={{ fontSize: '12px' }}>{(tile.w * tile.h).toLocaleString()} px</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Trust */}
          <div className="space-y-2 pt-1 border-t border-pink-500/10">
            {[
              { icon: FiShield, text: 'Permanent — never expires' },
              { icon: FiTrendingUp, text: 'Real SEO dofollow link' },
              { icon: FiLink, text: 'Your brand, forever visible' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-gray-400" style={{ fontSize: '14px' }}>
                <item.icon className="text-pink-500 shrink-0" /> {item.text}
              </div>
            ))}
          </div>

          {/* Nav links */}
          <div className="flex gap-2 pt-1">
            <button onClick={() => { setCurrentPage('rankings'); onClose(); }}
              className="flex-1 py-2.5 rounded-xl font-black uppercase tracking-wider flex items-center justify-center gap-1.5"
              style={{ border: '1px solid rgba(255,255,255,0.1)', color: '#9ca3af', fontSize: '13px' }}>
              <FiBarChart2 /> Rankings
            </button>
            <button onClick={() => { setCurrentPage('about'); onClose(); }}
              className="flex-1 py-2.5 rounded-xl font-black uppercase tracking-wider flex items-center justify-center gap-1.5"
              style={{ border: '1px solid rgba(255,255,255,0.1)', color: '#9ca3af', fontSize: '13px' }}>
              <FiInfo /> About
            </button>
          </div>

          {/* Auth */}
          {user ? (
            <div className="space-y-2">
              <button onClick={() => { setCurrentPage('myblocks'); onClose(); }}
                className="w-full py-3 rounded-xl font-black uppercase tracking-widest text-white flex items-center justify-center gap-2"
                style={{ background: 'rgba(255,0,200,0.12)', border: '1px solid rgba(255,0,200,0.3)', color: '#ff00cc', fontSize: '14px' }}>
                <FiGrid /> My Blocks
              </button>
              <div className="flex items-center justify-between p-3 rounded-xl"
                style={{ background: 'rgba(255,0,200,0.06)', border: '1px solid rgba(255,0,200,0.15)' }}>
                <div className="flex items-center gap-2">
                  {user.user_metadata?.avatar_url
                    ? <img src={user.user_metadata.avatar_url} alt="avatar" className="w-7 h-7 rounded-full" />
                    : <FiUser className="text-pink-400" />}
                  <span className="text-pink-300 font-bold truncate max-w-[160px]" style={{ fontSize: '14px' }}>
                    {user.user_metadata?.name || user.email?.split('@')[0]}
                  </span>
                </div>
                <button onClick={() => { signOut(); onClose(); }} className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors" style={{ fontSize: '13px' }}>
                  <FiLogOut /> Sign Out
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => { setIsAuthModalOpen(true); onClose(); }}
              className="w-full py-3.5 rounded-xl font-black uppercase tracking-widest text-white"
              style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)', fontSize: '15px', fontFamily: 'Impact,sans-serif' }}>
              Sign In / Sign Up
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MobileBar() {
  const { mode, setMode, pixelsSold } = useAppContext();
  const [sheetOpen, setSheetOpen] = useState(false);
  const currentPrice = getPricePerPixel(pixelsSold);

  return (
    <>
      {sheetOpen && <StatsSheet onClose={() => setSheetOpen(false)} />}
      <div className="fixed bottom-0 left-0 right-0 z-30 px-3 py-2"
        style={{ background: 'rgba(5,0,16,0.97)', borderTop: '1px solid rgba(255,0,200,0.25)', backdropFilter: 'blur(20px)' }}>
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-blink" />
            <span className="text-gray-400" style={{ fontSize: '12px' }}>Current price:</span>
            <span className="font-black text-white" style={{ fontSize: '14px' }}>${currentPrice.toFixed(2)}<span className="text-gray-500 font-normal">/px</span></span>
          </div>
          <button onClick={() => setSheetOpen(true)} className="flex items-center gap-1 text-gray-400 hover:text-pink-400 transition-colors" style={{ fontSize: '12px' }}>
            <FiTrendingUp style={{ fontSize: '14px' }} /> Stats
          </button>
        </div>
        <div className="flex items-center gap-2">
          <ModeToggle mode={mode} setMode={setMode} />
          <button onClick={() => setMode('select')} className="px-4 py-3 rounded-xl font-black uppercase tracking-wider text-white shrink-0"
            style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)', fontSize: '14px', fontFamily: 'Impact,sans-serif', boxShadow: '0 0 15px rgba(255,0,200,0.4)' }}>
            <FiZap />
          </button>
        </div>
        <div style={{ height: 'env(safe-area-inset-bottom,0px)' }} />
      </div>
    </>
  );
}